import {
  type ContentDeletionDeps,
  ContentDeletionService,
} from '@src/services/ContentDeletionService';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useRef, useState } from 'react';

import type { ContentItem } from './contentItems';

const logger = LogManager.getLogger('Home:useBulkDeletion');

interface UseBulkDeletionOptions extends ContentDeletionDeps {
  /**
   * Applies the deletion to the cached stories. A refetch can't do this —
   * see `removeContentItemsFromStories`.
   */
  onDeleted: (deleted: ContentItem[]) => void;
}

export function useBulkDeletion(deps: UseBulkDeletionOptions) {
  const videoCreator = useVideoCreatorStore();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const deletionServiceRef = useRef<ContentDeletionService | null>(null);
  const getDeletionService = useCallback(() => {
    if (!deletionServiceRef.current) {
      deletionServiceRef.current = new ContentDeletionService({
        storyRepository: deps.storyRepository,
        getHomeStories: deps.getHomeStories,
      });
    }
    return deletionServiceRef.current;
  }, [deps.storyRepository, deps.getHomeStories]);

  const confirmDelete = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const cancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const executeBulkDelete = useCallback(
    async (itemsToDelete: ContentItem[], onSuccess: () => void) => {
      setShowDeleteConfirm(false);
      setIsDeleting(true);

      try {
        const result =
          await getDeletionService().deleteContentItems(itemsToDelete);
        if (result.ok) {
          if (result.value.errors.length > 0) {
            videoCreator.showToast({
              state: 'warning',
              message: `Deleted ${result.value.deletedCount} item(s), but ${result.value.errors.length} failed`,
            });
          }
          onSuccess();
          // Only what actually went — a failed item must stay on the list.
          const failed = new Set(result.value.errors.map(e => e.id));
          deps.onDeleted(itemsToDelete.filter(item => !failed.has(item.id)));
        } else {
          videoCreator.showToast({
            state: 'error',
            message: result.error.message,
          });
        }
      } catch (e) {
        logger.error('Bulk delete failed', extractErrorInfo(e));
        videoCreator.showToast({
          state: 'error',
          message: 'An unexpected error occurred',
        });
      } finally {
        setIsDeleting(false);
      }
    },
    [getDeletionService, deps, videoCreator],
  );

  return {
    isDeleting,
    showDeleteConfirm,
    confirmDelete,
    cancelDelete,
    executeBulkDelete,
  };
}
